/**
 * React Query wrapper for provenance DAG data.
 */

import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchLineage, deleteNode, restoreNode, type DAGData, type FetchLineageOptions } from "../api/artifacts";

export function useProvenance(
  projectId: string,
  branch = "main",
  options: FetchLineageOptions = {},
) {
  return useQuery<DAGData>({
    queryKey: [
      "provenance",
      projectId,
      branch,
      options.scope ?? "all",
      options.includePlots ?? true,
      options.detail ?? "full",
    ],
    queryFn: () => fetchLineage(projectId, branch, options),
    placeholderData: keepPreviousData,
    // Provenance changes only on ingest — 60s is plenty.
    refetchInterval: 60_000,
  });
}

export function useDeleteNode(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nodeId: string) => deleteNode(nodeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provenance", projectId, branch] });
      qc.invalidateQueries({ queryKey: ["changelog", projectId] });
    },
  });
}

export function useRestoreNode(projectId: string, branch: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (nodeId: string) => restoreNode(nodeId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["provenance", projectId, branch] });
      qc.invalidateQueries({ queryKey: ["changelog", projectId] });
    },
  });
}
